import { Alert, InputAdornment, Typography } from '@mui/material';
import { OLstvToken } from 'modules/api';
import { fromSatoshi } from 'modules/api/utils/convertSatoshi';
import { useConnection } from 'modules/auth';
import { AmountInput } from 'modules/common/components/AmountInput';
import {
  BTC_DECIMALS,
  DECIMAL_PLACES_BTC,
  SECURITY_FEE,
} from 'modules/common/const';
import { useValidateAmount } from 'modules/common/hooks/useValidateAmount';
import { useTranslation } from 'modules/i18n';
import { ReactNode, useCallback } from 'react';
import { UseFormReturn, useController } from 'react-hook-form';
import { IWithdrawalFormValues } from '../../hooks/useWithdrawalForm';
import { translation } from './translation';
import { useVaultBalance } from '../../hooks/useVaultBalance';

type AmountFieldProps = Pick<
  UseFormReturn<IWithdrawalFormValues>,
  'control' | 'setValue' | 'watch'
> & {
  disabled?: boolean;
  startAdornment?: ReactNode;
};

const minAmount = SECURITY_FEE.plus(fromSatoshi(1));

export function AmountField({
  disabled,
  control,
  setValue,
  startAdornment,
}: AmountFieldProps): JSX.Element {
  const { isConnected } = useConnection();
  const { keys, t } = useTranslation(translation);
  const { balance } = useVaultBalance();

  const validateAmount = useValidateAmount({
    balance,
    minAmount,
  });

  const {
    field,
    fieldState: { error },
  } = useController({
    name: 'amount',
    control,
    rules: {
      validate: validateAmount,
    },
  });

  const handleMaxClick = useCallback(() => {
    const amount = balance.toFixed();

    setValue('amount', amount, {
      shouldValidate: true,
    });
  }, [balance, setValue]);

  return (
    <AmountInput
      {...field}
      labelText={
        <Typography fontWeight={500}>{t(keys.chooseAsset)}</Typography>
      }
      error={!!error}
      helperAlert={
        error && (
          <Alert severity="error" variant="filled">
            {error?.message}
          </Alert>
        )
      }
      balance={isConnected ? balance : undefined}
      balanceText={t(keys.balanceText)}
      token={OLstvToken.sonicLBTCv}
      onMaxClick={isConnected ? handleMaxClick : undefined}
      decimalPlaces={DECIMAL_PLACES_BTC}
      isDisabled={disabled}
      maxDecimalsLen={BTC_DECIMALS}
      InputProps={{
        sx: {
          height: 80,
        },
        startAdornment: startAdornment ? (
          <InputAdornment position="start">{startAdornment}</InputAdornment>
        ) : undefined,
      }}
    />
  );
}
